import { useState } from 'react';
import { usePlatform } from '../../context/PlatformContext';
import { useDashboardData } from '../../hooks/useDashboardData';
import GlobalHeader from './GlobalHeader';
import ExecutiveSummary from './ExecutiveSummary';
import SmartAlertsPanel from './SmartAlertsPanel';
import AIInsightsPanel from './AIInsightsPanel';
import PerformanceTrends from './PerformanceTrends';
import VersionIntelligenceTable from './VersionIntelligenceTable';
import VersionDrilldownPanel from './VersionDrilldownPanel';
import DeveloperView from './DeveloperView';

/**
 * UnifiedDashboard — Main orchestrator.
 * Executive Mode: KPIs → Alerts → AI Insights → Trends → Version Table → Drilldown
 * Developer Mode: Quick Stats → AI Recs → Crash Clusters → ANR Breakdown
 * Shared: GlobalHeader with platform/version/environment/viewMode filters.
 */

export default function UnifiedDashboard() {
  const { platform, version, viewMode } = usePlatform();
  const [selectedVersion, setSelectedVersion] = useState(null);

  const {
    loading, error,
    // Executive
    summary, trend, trendDays, readiness, platformSummary, rootCauses, alerts, platformRisk,
    // Developer
    crashes, heatmap, anr, aiRecs, devSummary, buildBreakdown,
    // Versions
    versions, liveStatus,
    // Actions
    changeTrendDays, refresh,
  } = useDashboardData({ platform, version, viewMode });

  return (
    <div className="space-y-4 -m-6">
      {/* Sticky Global Header */}
      <GlobalHeader
        versions={versions}
        onRefresh={refresh}
      />

      {/* Main content area */}
      <div className="px-6 pb-6 space-y-4">
        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
              <span className="text-sm text-gray-400">Loading dashboard...</span>
            </div>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
            <p className="text-sm text-red-400">Failed to load data: {error}</p>
            <button
              onClick={refresh}
              className="text-xs text-red-300 underline mt-1 hover:text-red-200"
            >
              Retry
            </button>
          </div>
        )}

        {/* Executive View */}
        {!loading && viewMode === 'executive' && (
          <>
            {/* Row 1: 6 KPI Cards */}
            <ExecutiveSummary
              summary={summary}
              platformRisk={platformRisk}
              platformSummary={platformSummary}
            />

            {/* Row 2: Alerts + AI Insights — 3 equal columns */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <SmartAlertsPanel alerts={alerts} />
              <AIInsightsPanel rootCauses={rootCauses} />
            </div>

            {/* Row 3: Performance Trends */}
            <PerformanceTrends
              trend={trend}
              trendDays={trendDays}
              onChangeDays={changeTrendDays}
            />

            {/* Row 4: Version Intelligence Table */}
            <VersionIntelligenceTable
              versions={versions}
              readiness={readiness}
              onSelectVersion={(v) => setSelectedVersion(v)}
            />

            {/* Slide-in Drilldown Panel */}
            {selectedVersion && (
              <>
                {/* Backdrop */}
                <div
                  className="fixed inset-0 bg-black/40 z-30"
                  onClick={() => setSelectedVersion(null)}
                />
                <VersionDrilldownPanel
                  version={selectedVersion}
                  onClose={() => setSelectedVersion(null)}
                />
              </>
            )}
          </>
        )}

        {/* Developer View */}
        {!loading && viewMode === 'developer' && (
          <DeveloperView
            crashes={crashes}
            heatmap={heatmap}
            anr={anr}
            aiRecs={aiRecs}
            devSummary={devSummary}
            buildBreakdown={buildBreakdown}
          />
        )}

        {/* Fallback: if no viewMode-specific content and not loading */}
        {!loading && viewMode !== 'executive' && viewMode !== 'developer' && (
          <div className="text-center text-gray-500 py-12">
            <p className="text-sm">Select a view mode to get started</p>
          </div>
        )}
      </div>
    </div>
  );
}
